import { NULL, TRUE, FALSE } from "constants/primitives";
import { IConsentContext } from "core/UserConsentContext";
import { PanelTab } from "types/config-files/mainConfig";
import WidgetConfig from "helpers/init/WidgetConfig";
import { ISiteCookiesApiResponse } from "helpers/fetchSiteCookies";
import CookieFirstPublicApi, {
  createCookieFirstPublicApiInstance,
} from "./CookieFirstPublicApi";
import { ITCFConsentContext } from "helpers/tcf/TCFConsentProvider/TCFUserConsentContext";
import {
  ITCFConsent,
  ITCFVendorCategory,
} from "helpers/tcf/TCFConsentProvider/types";
import {
  ITCFGatpVendor,
  ITCFGatpVendorList,
  ITCFGlobalVendorList,
  ITCFVendor,
} from "types/tcf";
import {
  MISSING_GATP,
  MISSING_GVL,
  NEW_CONSENT_INVALID_TYPE,
  WRONG_TCF_MULTI_CATEGORY_DATA,
  WRONG_TCF_SINGLE_CATEGORY_DATA,
} from "constants/errors";
import { LOGS_PREFIX } from "core/config";
import isArray from "helpers/isArray";
import { IConsent } from "types/consent";
import { calculateTCFBaseConsent } from "helpers/tcf/tcfHelpers/calculateTCFBaseConsent";
import isObject from "helpers/isObject";
import { NUMBER_0, NUMBER_1 } from "constants/numbers";

export interface CookieFirstPublicApiTCF
  extends Omit<CookieFirstPublicApi, "updateConsent"> {
  TCFconsent: ITCFConsent | null;
  TCFeventStatus: string;
  TCFcountry: string;
  TCFgvl: ITCFGlobalVendorList | null;
  TCFgatp: ITCFGatpVendorList | null;
  TCFisEnabled: boolean;
  TCFgetConsentStructure: () => ITCFConsent;
  TCFacceptCategory: (categories: PublicApiTCFCategory) => Promise<void>;
  TCFdeclineCategory: (categories: PublicApiTCFCategory) => Promise<void>;
  TCFacceptCategoryLegitimateInterest: (
    categories: PublicApiTCFCategory
  ) => Promise<void>;
  TCFdeclineCategoryLegitimateInterest: (
    categories: PublicApiTCFCategory
  ) => Promise<void>;
  TCFacceptVendor: (id: number | number[]) => Promise<void>;
  TCFdeclineVendor: (id: number | number[]) => Promise<void>;
  TCFacceptVendorLegitimateInterest: (id: number | number[]) => Promise<void>;
  TCFdeclineVendorLegitimateInterest: (id: number | number[]) => Promise<void>;
  TCFisVendorAccepted: (id: number) => boolean;
  TCFisVendorLegitimateInterestAccepted: (id: number) => boolean;
  TCFisCategoryAccepted: (name: ITCFVendorCategory, id: number) => boolean;
  TCFisCategoryLegitimateInterestAccepted: (
    name: ITCFVendorCategory,
    id: number
  ) => boolean;
  TCFacceptGatpVendor: (id: number | number[]) => Promise<void>;
  TCFdeclineGatpVendor: (id: number | number[]) => Promise<void>;
  TCFisGatpVendorAccepted: (id: number) => boolean;
  updateConsent: (consent: IConsent, tcf: ITCFConsent) => Promise<void>;
}

export const createCookieFirstPublicApiTCFInstance = (
  deps: PublicApiTCFDependencies
): CookieFirstPublicApiTCF => {
  // prepare dependencies and helpers
  const _dep = deps;
  const gvl = _dep.gvl;
  const gatp = _dep.gatp;
  const consentCtx = _dep.consent;
  const TCFConsentCtx = _dep.TCFConsent;
  const widgetConfig = deps.widgetConfig;
  const hasConsented = !!consentCtx.hasConsented && !!consentCtx.hasConsented;
  let TCFConsent: ITCFConsent | null = NULL;

  // Trigger save of default consent because that's the only way to save gvl and move banner to "hasConsented" mode
  // Since we alter only tcf, in order to trigger save we use default consent or already saved if there is any already saved.
  const triggerConsentSave = () => {
    return consentCtx.handleConsent(
      consentCtx.userConsent ? consentCtx.userConsent : consentCtx.baseConsent
    );
  };

  /**
   * Find information about vendor and return object with it's data
   */
  const getVendorData = (id: number): ITCFVendor | null => {
    if (!gvl) {
      console.warn(MISSING_GVL);
      return NULL;
    }

    const vendor = Object.values(gvl.vendors).find((e) => e.id === id);

    if (vendor) return vendor;

    console.error(
      `${LOGS_PREFIX}  Missing vendor in GVL with given id (${id})`
    );
    return NULL;
  };

  /**
   * Find information about GATP vendor and return object with it's data
   */
  const getGatpVendorData = (id: number): ITCFGatpVendor | null => {
    if (!gatp) {
      console.warn(MISSING_GATP);
      return NULL;
    }

    const vendor = Object.values(gatp).find(
      (e) => Number(e.provider_id) === id
    );

    if (vendor) return vendor;

    console.error(
      `${LOGS_PREFIX}  Missing vendor in GATP with given id (${id})`
    );
    return NULL;
  };

  const updateVendorHelper = (
    id: number | number[],
    functionName: "setVendor" | "setVendorLegitimateInterest" | "setGatpVendor",
    value: boolean
  ): Promise<void> => {
    // Const with names of possible functions
    const FUNC_VENDOR = "setVendor",
      FUNC_VENDOR_LEG_INT = "setVendorLegitimateInterest",
      FUNC_VENDOR_GATP = "setGatpVendor";

    // Determinate which functions use gvl and which gatp
    switch (functionName) {
      case FUNC_VENDOR:
      case FUNC_VENDOR_LEG_INT:
        if (gvl) {
          // Determinate if there is only one vendor to be saved or multiple
          if (isArray(id)) {
            // Variable to determinate if at least one vendor has been updated
            let anyVendorUpdated = FALSE;
            for (let i = 0; i < id.length; i++) {
              const vendor = getVendorData(id[i]);

              if (vendor) {
                anyVendorUpdated = TRUE;
                TCFConsentCtx[functionName](vendor, value, gvl);
              }
            }

            if (anyVendorUpdated) {
              return triggerConsentSave();
            }
          } else {
            const vendor = getVendorData(id);

            if (vendor) {
              TCFConsentCtx[functionName](vendor, value, gvl);
              return triggerConsentSave();
            }
          }
        } else {
          console.warn(MISSING_GVL);
          return Promise.resolve();
        }

        break;

      case FUNC_VENDOR_GATP:
        if (gatp) {
          // Determinate if there is only one vendor to be saved or multiple
          if (isArray(id)) {
            // Variable to determinate if at least one vendor has been updated
            let anyVendorUpdated = FALSE;
            for (let i = NUMBER_0; i < id.length; i++) {
              const vendor = getGatpVendorData(id[i]);

              if (vendor) {
                anyVendorUpdated = TRUE;
                TCFConsentCtx[functionName](vendor, value);
              }
            }

            if (anyVendorUpdated) {
              return triggerConsentSave();
            }
          } else {
            const vendor = getGatpVendorData(id);

            if (vendor) {
              TCFConsentCtx[functionName](vendor, value);
              return triggerConsentSave();
            }
          }
        } else {
          console.warn(MISSING_GATP);
          return Promise.resolve();
        }

        break;
    }

    return Promise.resolve();
  };

  const updateCategoryHelper = (
    categories: PublicApiTCFCategory,
    functionName: "setCategoryValue" | "setCategoryLegitimateInterestValue",
    value: boolean
  ): Promise<void> => {
    if (gvl) {
      // Determinate if there is only one vendor to be saved or multiple
      if (isArray(categories[NUMBER_0])) {
        // Variable to determinate if at least one vendor has been updated
        let anyVendorUpdated = FALSE;
        for (let i = NUMBER_0; i < categories.length; i++) {
          const category = categories[i];

          if (isArray(category)) {
            TCFConsentCtx[functionName](
              category[NUMBER_0],
              category[NUMBER_1],
              value,
              gvl
            );
            anyVendorUpdated = TRUE;
          } else {
            console.error(WRONG_TCF_MULTI_CATEGORY_DATA);
          }
        }

        if (anyVendorUpdated) {
          return triggerConsentSave();
        }
      } else {
        if (
          typeof categories[NUMBER_0] === "string" &&
          typeof categories[1] === "number"
        ) {
          TCFConsentCtx[functionName](
            categories[NUMBER_0],
            categories[NUMBER_1],
            value,
            gvl
          );
          return triggerConsentSave();
        } else {
          console.error(WRONG_TCF_SINGLE_CATEGORY_DATA);
        }
      }

      return Promise.resolve();
    } else {
      console.warn(MISSING_GVL);
      return Promise.resolve();
    }
  };

  if (hasConsented) {
    TCFConsent = TCFConsentCtx.userConsent;
  }

  // Get final object from default public api
  const publicApi: CookieFirstPublicApi = createCookieFirstPublicApiInstance({
    setPanelTab: _dep.setPanelTab,
    consent: _dep.consent,
    widgetConfig,
    stealthMode: _dep.stealthMode,
    fetchLatestScan: _dep.fetchLatestScan,
    setUserLang: _dep.setUserLang,
  });

  // Inject tcf elements into final object
  const publicApiTCF: CookieFirstPublicApiTCF = {
    ...publicApi,
    TCFconsent: TCFConsent,
    TCFeventStatus: TCFConsentCtx.eventStatus,
    TCFcountry: TCFConsentCtx.tcfCountry,
    TCFgvl: gvl,
    TCFgatp: gatp,
    TCFisEnabled: widgetConfig.tcfEnabled,
    TCFgetConsentStructure() {
      return calculateTCFBaseConsent();
    },
    TCFacceptCategory(categories: PublicApiTCFCategory) {
      return updateCategoryHelper(categories, "setCategoryValue", TRUE);
    },
    TCFdeclineCategory(categories: PublicApiTCFCategory) {
      return updateCategoryHelper(categories, "setCategoryValue", FALSE);
    },
    TCFacceptCategoryLegitimateInterest(categories: PublicApiTCFCategory) {
      return updateCategoryHelper(
        categories,
        "setCategoryLegitimateInterestValue",
        TRUE
      );
    },
    TCFdeclineCategoryLegitimateInterest(categories: PublicApiTCFCategory) {
      return updateCategoryHelper(
        categories,
        "setCategoryLegitimateInterestValue",
        FALSE
      );
    },
    TCFacceptVendor(id: number | number[]) {
      return updateVendorHelper(id, "setVendor", TRUE);
    },
    TCFdeclineVendor(id: number | number[]) {
      return updateVendorHelper(id, "setVendor", FALSE);
    },
    TCFacceptVendorLegitimateInterest(id: number | number[]) {
      return updateVendorHelper(id, "setVendorLegitimateInterest", TRUE);
    },
    TCFdeclineVendorLegitimateInterest(id: number | number[]) {
      return updateVendorHelper(id, "setVendorLegitimateInterest", FALSE);
    },
    TCFisVendorAccepted(id: number) {
      if (gvl) {
        // Find right vendor data
        const vendor = getVendorData(id);

        if (vendor) {
          return TCFConsentCtx.isVendorAccepted(vendor);
        } else {
          return FALSE;
        }
      } else {
        console.warn(MISSING_GVL);
        return FALSE;
      }
    },
    TCFisVendorLegitimateInterestAccepted(id: number) {
      if (gvl) {
        // Find right vendor data
        const vendor = getVendorData(id);

        if (vendor) {
          return TCFConsentCtx.isVendorLegitimateInterestAccepted(vendor);
        } else {
          return FALSE;
        }
      } else {
        console.warn(MISSING_GVL);
        return FALSE;
      }
    },
    TCFisCategoryAccepted(name: ITCFVendorCategory, id: number) {
      if (gvl) {
        return TCFConsentCtx.isCategoryAccepted(
          name,
          id,
          TCFConsentCtx.userConsent
        );
      } else {
        console.warn(MISSING_GVL);
        return FALSE;
      }
    },
    TCFisCategoryLegitimateInterestAccepted(
      name: ITCFVendorCategory,
      id: number
    ) {
      if (gvl) {
        return TCFConsentCtx.isCategoryLegitimateInterestAccepted(
          name,
          id,
          TCFConsentCtx.userConsent
        );
      } else {
        console.warn(MISSING_GVL);
        return FALSE;
      }
    },
    TCFacceptGatpVendor(id: number | number[]) {
      return updateVendorHelper(id, "setGatpVendor", TRUE);
    },
    TCFdeclineGatpVendor(id: number | number[]) {
      return updateVendorHelper(id, "setGatpVendor", FALSE);
    },
    TCFisGatpVendorAccepted(id: number) {
      if (gatp) {
        const vendor = getGatpVendorData(id);

        if (vendor) {
          TCFConsentCtx.isGatpVendorAccepted(vendor);
        }
      } else {
        console.warn(MISSING_GATP);
      }

      return FALSE;
    },
    updateConsent(consent: IConsent = {}, tcf: ITCFConsent) {
      if (!isObject(consent) || (tcf && !isObject(tcf))) {
        throw new Error(NEW_CONSENT_INVALID_TYPE);
      }

      // We allow update of tcf to be optional in order to not break already existing clients scripts.
      if (tcf) {
        TCFConsentCtx.setConsent(tcf);
      }

      return consentCtx.handleConsent(consent);
    },
  };

  return publicApiTCF;
};

export interface PublicApiTCFDependencies {
  setPanelTab: (mewTab?: PanelTab | string | null) => void;
  setUserLang: (newLang: string) => void;
  consent: IConsentContext;
  TCFConsent: ITCFConsentContext;
  widgetConfig: WidgetConfig;
  stealthMode: boolean;
  fetchLatestScan: () => Promise<ISiteCookiesApiResponse>;
  gvl: ITCFGlobalVendorList | null;
  gatp: ITCFGatpVendorList | null;
}

export type PublicApiTCFCategory =
  | [ITCFVendorCategory, number]
  | [ITCFVendorCategory, number][];

export default CookieFirstPublicApiTCF;
